import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Building2,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Download,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  Briefcase,
  Upload,
  RefreshCw,
  Globe,
  Plus
} from 'lucide-react';
import { Label } from '../ui/label';
import { useConfirmDialog } from '../ui/confirm-dialog';
import { toast } from 'sonner';
import { normalizeMediaUrl } from '@/utils/media';
import { Textarea } from '../ui/textarea';
import { apiClient } from '../../lib/api-client';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import companyParameterService from '../../services/companyParameterService';
import { useLocation } from 'react-router-dom';
import { useData } from '../../context/DataContext';

const boxLabelCls = 'px-2 text-gray-700 text-sm font-semibold border-r h-full flex items-center whitespace-nowrap';
const fieldCls = 'w-full h-10 px-3 py-2 rounded-md border border-gray-200 bg-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300';
type BoxProps = { label: string; children: React.ReactNode; noBorder?: boolean };
const Box = ({ label, children, noBorder }: BoxProps) => (
  <div className={`w-full bg-gray-50 ${noBorder ? 'border-0' : 'border border-gray-200'} rounded-md h-10 flex items-center px-2 gap-2`}>
    <div className={boxLabelCls}>{label}</div>
    <div className="flex-1 h-full">
      {children}
    </div>
  </div>
);
const BoxAuto = ({ label, children }: BoxProps) => (
  <div className="w-full bg-gray-50 border border-gray-200 rounded-md p-2">
    <div className="text-gray-700 text-sm font-semibold px-1">{label}</div>
    <div className="mt-2">
      {children}
    </div>
  </div>
);
const BoxPlain = ({ label, children }: BoxProps) => (
  <div className="w-full">
    <label className="block text-sm font-medium mb-1 text-gray-700">{label}</label>
    {children}
  </div>
);
type Branch = {
  id: string;
  name: string;
  city: string;
  address: string;
  phone?: string;
  employees?: number;
  is_headquarters?: boolean;
};

type Company = {
  id?: string | number;
  _id?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  location?: string;
  industry?: string;
  website?: string;
  description?: string;
  status?: string;
  verified?: boolean;
  logo_url?: string;
  logo?: string;
  logoUrl?: string;
  profileImage?: string;
  is_featured?: boolean;
  is_top_hiring?: boolean;
  company_size?: string;
  activeJobs?: number;
  totalJobs?: number;
  totalApplications?: number;
  branches?: Branch[];
};

type Job = {
  id?: string | number;
  _id?: string | number;
  title?: string;
  company?: string | { name?: string };
  status?: string;
};

type Application = {
  jobId?: string;
  job_id?: string;
};

type FormDataState = {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  country: string;
  postal_code: string;
  website: string;
  description: string;
  industry: string;
  company_size: string;
  verified: boolean;
  status: string;
  visibility: 'default' | 'featured' | 'top_hiring';
  branches: Branch[];
};

interface EmployerManagementNewProps {
  companies: Company[];
  jobs: Job[];
  applications: Application[];
  onCompanyUpdate: (companies: Company[]) => void;
}

export function EmployerManagementNew({ companies, jobs, applications, onCompanyUpdate }: EmployerManagementNewProps) {
  const location = useLocation();
  const { adminCities, adminCitiesByProvince, adminCountries, adminProvinces } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('verification') || params.get('preset');
    if (v === 'verified') return 'verified';
    if (v === 'unverified' || v === 'verification-queue') return 'unverified';
    return 'all';
  });
  const [industryFilter, setIndustryFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'list' | 'verification' | 'add' | 'edit' | 'profile'>(() => {
    const params = new URLSearchParams(window.location.search);
    const preset = params.get('preset');
    if (preset === 'verification-queue') return 'verification';
    return 'list';
  });
  const DEFAULT_INDUSTRIES = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 'Retail',
    'Construction', 'Transportation', 'Hospitality', 'Media', 'Government', 'Non-profit',
    'Energy', 'Agriculture', 'Other'
  ];
  const [industryOptions, setIndustryOptions] = useState<string[]>(DEFAULT_INDUSTRIES);
  // Location datasets (seeded for Nepal only, alphabetically ordered)
  const COUNTRIES_FALLBACK = ['Nepal'];
  const PROVINCES_BY_COUNTRY: Record<string, string[]> = {
    Nepal: ['Bagmati', 'Gandaki', 'Karnali', 'Koshi', 'Lumbini', 'Madhesh', 'Sudurpashchim'].sort((a, b) => a.localeCompare(b))
  };
  const CITIES_NEPAL_BY_PROVINCE: Record<string, string[]> = {
    Bagmati: ['Kathmandu', 'Lalitpur', 'Bhaktapur', 'Hetauda', 'Banepa'].sort((a, b) => a.localeCompare(b)),
    Gandaki: ['Pokhara', 'Gorkha', 'Lamjung', 'Syangja', 'Tanahun'].sort((a, b) => a.localeCompare(b)),
    Karnali: ['Surkhet', 'Jumla', 'Dailekh', 'Jajarkot', 'Dolpa'].sort((a, b) => a.localeCompare(b)),
    Koshi: ['Biratnagar', 'Dharan', 'Itahari', 'Inaruwa', 'Birtamode'].sort((a, b) => a.localeCompare(b)),
    Lumbini: ['Butwal', 'Bhairahawa', 'Tansen', 'Nepalgunj', 'Kapilvastu'].sort((a, b) => a.localeCompare(b)),
    Madhesh: ['Janakpur', 'Birgunj', 'Kalaiya', 'Simara', 'Gaur'].sort((a, b) => a.localeCompare(b)),
    Sudurpashchim: ['Dhangadhi', 'Mahendranagar', 'Dadeldhura', 'Baitadi', 'Amargadhi'].sort((a, b) => a.localeCompare(b))
  };
  const [provinceOptions, setProvinceOptions] = useState<string[]>(PROVINCES_BY_COUNTRY['Nepal']);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [isCustomCountry, setIsCustomCountry] = useState(false);
  const [isCustomProvince, setIsCustomProvince] = useState(false);
  const [isCustomCity, setIsCustomCity] = useState(false);
  // New modal and form state
  // Inline tab replaces previous add modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [initialFormData, setInitialFormData] = useState<FormDataState>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    country: 'Nepal',
    postal_code: '',
    website: '',
    description: '',
    industry: '',
    company_size: '',
    verified: false,
    status: 'pending',
    visibility: 'default',
    branches: []
  });
  const [formData, setFormData] = useState<FormDataState>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    country: 'Nepal',
    postal_code: '',
    website: '',
    description: '',
    industry: '',
    company_size: '',
    verified: false,
    status: 'pending',
    visibility: 'default',
    branches: []
  });

  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);

  // Load industry options from service
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const items = await companyParameterService.list('industry');
        const names = Array.isArray(items)
          ? items.map((it: any) => (typeof it === 'string' ? it : it?.name)).filter(Boolean)
          : [];
        if (mounted && names.length) setIndustryOptions(names as string[]);
      } catch { }
    })();
    return () => { mounted = false; };
  }, []);

  // Reset form when switching to Add tab to avoid leaking edit data
  const makeEmptyForm = (): FormDataState => ({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    country: 'Nepal',
    postal_code: '',
    website: '',
    description: '',
    industry: '',
    company_size: '',
    verified: false,
    status: 'pending',
    visibility: 'default',
    branches: []
  });

  useEffect(() => {
    if (activeTab === 'add') {
      const empty = makeEmptyForm();
      setSelectedCompany(null);
      setFormData(empty);
      setInitialFormData(empty);
      setLogoFile(null);
      setEditingBranchId(null);
      setIsCustomCountry(false);
      setIsCustomProvince(false);
      setIsCustomCity(false);
    }
  }, [activeTab]);

  // React to country selection (admin-managed provinces first)
  useEffect(() => {
    const country = String(formData.country || 'Nepal');
    let provinces: string[] = [];
    if (Array.isArray(adminProvinces) && adminProvinces.length > 0) {
      provinces = adminProvinces;
    } else {
      provinces = PROVINCES_BY_COUNTRY[country] || [];
    }
    setProvinceOptions(provinces);
    if (!provinces.includes(String(formData.province || ''))) {
      setFormData(prev => ({ ...prev, province: '' }));
    }
  }, [formData.country, adminProvinces]);

  // React to province selection
  useEffect(() => {
    let cities: string[] = [];
    const prov = String(formData.province || '').trim();

    if (prov) {
      // Try exact match first
      if (adminCitiesByProvince && adminCitiesByProvince[prov]?.length) {
        cities = adminCitiesByProvince[prov];
      } else if (adminCitiesByProvince) {
        // Fallback to case-insensitive match
        const matchingKey = Object.keys(adminCitiesByProvince).find(k => k.toLowerCase() === prov.toLowerCase());
        if (matchingKey) {
          cities = adminCitiesByProvince[matchingKey];
        }
      }
    } else {
      if (Array.isArray(adminCities) && adminCities.length > 0) {
        cities = adminCities;
      }
    }

    setCityOptions(cities);
    // Don't auto-clear if it already matches one of the new options
    if (cities.length && formData.city && !cities.includes(formData.city)) {
      // setFormData(prev => ({ ...prev, city: '' })); // Disabled auto-clear to avoid frustrating UX
    }
  }, [formData.country, formData.province, adminCities, adminCitiesByProvince]);

  const safeString = (val: any) => (typeof val === 'string' ? val : '');
  const getCompanyId = (c: Company) => String(c.id ?? c._id ?? '');
  const getCompanyName = (c: Company) => String(c.name ?? (c as any).company_name ?? (c as any).companyName ?? '');
  const normalizedCompanyLogo = (c: Company) => {
    const raw = (c as any).logo_url || (c as any).logoUrl || (c as any).logo || (c as any).company_logo || (c as any).companyLogo || '';
    const normalized = normalizeMediaUrl(raw) || raw;
    const s = String(normalized || '').trim();
    if (s) return s;
    const id = getCompanyId(c);
    if (!id) return '';
    return normalizeMediaUrl(`/uploads/company-logos/${id}`) || `/uploads/company-logos/${id}`;
  };

  // Apply URL preset to adjust view; default stays 'verified'
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preset = params.get('preset');
    if (preset === 'verification-queue' || preset === 'unverified') {
      setActiveTab('verification');
      setVerificationFilter('unverified');
    } else if (preset === 'verified') {
      setActiveTab('list');
      setVerificationFilter('verified');
    } else if (preset === 'all') {
      setActiveTab('list');
      setVerificationFilter('all');
    }
  }, [location.search]);

  // Enhanced company data using actual counts (no mock values)
  type ExtendedCompany = Company & { registrationDate?: string; lastActivity?: string; employeeCount?: number };
  const enhancedCompanies: ExtendedCompany[] = companies.map((company) => {
    const name = getCompanyName(company);
    const jobCompany = (j: Job) => {
      const v = typeof j?.company === 'string' ? (j?.company as string) : (j?.company as any)?.name;
      return safeString(v);
    };
    const companyJobs = jobs.filter(job => jobCompany(job) === name);
    const totalJobs = companyJobs.length;
    const activeJobs = companyJobs.filter(job => safeString(job?.status) === 'active').length;
    const totalApplications = applications.filter(app => {
      const job = jobs.find((j: any) => String(j?.id ?? j?._id ?? '') === String(app?.jobId ?? app?.job_id ?? ''));
      return job && jobCompany(job) === name;
    }).length;

    return {
      ...company,
      totalJobs,
      activeJobs,
      totalApplications,
      verified: !!(((company as any)?.verified ?? (company as any)?.is_verified ?? (company as any)?.isVerified)),
      registrationDate: safeString((company as any)?.registrationDate || (company as any)?.createdAt || (company as any)?.created_at),
      lastActivity: safeString((company as any)?.lastActivity || (company as any)?.updatedAt || (company as any)?.updated_at),
      phone: safeString(company?.phone),
      website: safeString(company?.website),
      employeeCount: Number((company as any)?.employeeCount ?? 0),
      industry: safeString(company?.industry),
      status: safeString(company?.status || 'active'),
      name
    };
  });

  const filteredCompanies = enhancedCompanies.filter(company => {
    const name = getCompanyName(company);
    const location = safeString(company?.location);
    const industry = safeString(company?.industry);
    const query = searchTerm.toLowerCase();
    const matchesSearch = [name, location, industry].some(field => field.toLowerCase().includes(query));
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesVerification = verificationFilter === 'all' ||
      (verificationFilter === 'verified' && company.verified) ||
      (verificationFilter === 'unverified' && !company.verified);
    const matchesIndustry = industryFilter === 'all' || industry === industryFilter;
    return matchesSearch && matchesStatus && matchesVerification && matchesIndustry;
  });

  // Pagination for Companies table
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage) || 1;
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, verificationFilter, industryFilter, itemsPerPage]);

  // Upload logo helper
  const uploadLogoForCompany = async (companyId: string) => {
    if (!logoFile) return null;
    try {
      const updated = await apiClient.uploadCompanyLogoByAdmin(String(companyId), logoFile);
      return updated || null;
    } catch (e) {
      console.error('Logo upload failed:', e);
      return null;
    }
  };

  // Create company via admin API
  const handleAddCompany = async () => {
    setLoading(true);
    try {
      const trimmedName = (formData.name || '').trim();
      const trimmedEmail = (formData.email || '').trim();
      if (!trimmedName || !trimmedEmail) {
        toast.error('Name and email are required');
        setLoading(false);
        return;
      }

      try {
        const serverCompanies = await apiClient.getAdminCompanies({ search: trimmedEmail });
        const existsExact = (serverCompanies || []).some((c: any) => String(c?.email || '').toLowerCase() === trimmedEmail.toLowerCase());
        if (existsExact) {
          toast('Email already exists for a company. Proceeding');
        }
      } catch { }

      const addressParts = [formData.address, formData.city, formData.province, formData.country]
        .map(s => String(s || '').trim())
        .filter(Boolean);
      const compositeAddress = addressParts.join(', ');
      const payload = {
        ...formData,
        name: trimmedName,
        email: trimmedEmail,
        phone: (formData.phone || '').trim(),
        address: compositeAddress || (formData.address || '').trim(),
        website: (formData.website || '').trim(),
        description: (formData.description || '').trim(),
        industry: (formData.industry || '').trim(),
        company_size: (formData.company_size || '').trim(),
        size: (formData.company_size || '').trim(),
        location: compositeAddress || (formData.address || '').trim(),
        city: (formData.city || '').trim() || undefined,
        province: (formData.province || '').trim() || undefined,
        country: (formData.country || '').trim() || undefined,
        postal_code: (formData.postal_code || '').trim() || undefined,
        is_featured: formData.visibility === 'featured',
        featured: formData.visibility === 'featured',
        is_top_hiring: formData.visibility === 'top_hiring',
        top_hiring: formData.visibility === 'top_hiring',
        branches: formData.branches
      };

      let newCompany = await apiClient.createCompanyByAdmin(payload);
      const logoUpdated = await uploadLogoForCompany(getCompanyId(newCompany));
      if (logoUpdated) newCompany = logoUpdated;
      onCompanyUpdate([...(companies || []), newCompany]);
      try {
        const refreshed = await apiClient.getAdminCompanies({ limit: 1000 });
        onCompanyUpdate(Array.isArray(refreshed) ? refreshed : []);
      } catch { }
      setActiveTab('list');
      setFormData({
        name: '', email: '', phone: '', address: '', city: '', province: '', country: '', postal_code: '', website: '', description: '', industry: '', company_size: '', verified: false, status: 'pending', visibility: 'default', branches: []
      });
      setLogoFile(null);
      toast.success('Company added successfully');
    } catch (error: any) {
      const msg = String(error?.message || '').toLowerCase();
      console.error('Error adding company:', error);
      toast.error(error?.message || 'Failed to add company');
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal with selected company
  const openEditModal = (company: any) => {
    setSelectedCompany(company);
    setFormData({
      name: safeString(company?.name),
      email: safeString(company?.email),
      phone: safeString(company?.phone),
      address: safeString(company?.location || company?.address),
      city: safeString((company as any)?.city),
      province: safeString((company as any)?.province),
      country: safeString((company as any)?.country),
      postal_code: safeString((company as any)?.postal_code),
      website: safeString(company?.website),
      description: safeString(company?.description),
      industry: safeString(company?.industry),
      company_size: safeString(company?.company_size),
      verified: Boolean(company?.verified),
      status: safeString(company?.status || 'active'),
      visibility: company?.is_featured ? 'featured' : (company?.is_top_hiring ? 'top_hiring' : 'default'),
      branches: company?.branches || []
    });
    setInitialFormData({
      name: safeString(company?.name),
      email: safeString(company?.email),
      phone: safeString(company?.phone),
      address: safeString(company?.location || company?.address),
      city: safeString((company as any)?.city),
      province: safeString((company as any)?.province),
      country: safeString((company as any)?.country),
      postal_code: safeString((company as any)?.postal_code),
      website: safeString(company?.website),
      description: safeString(company?.description),
      industry: safeString(company?.industry),
      company_size: safeString(company?.company_size),
      verified: Boolean(company?.verified),
      status: safeString(company?.status || 'active'),
      visibility: company?.is_featured ? 'featured' : (company?.is_top_hiring ? 'top_hiring' : 'default'),
      branches: company?.branches || []
    });
    setActiveTab('edit');
  };

  // Update company via admin API
  const handleEditCompany = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const id = getCompanyId(selectedCompany);
      const payload: any = {
        ...formData,
        // mirror fields for backend compatibility
        location: formData.address || '',
        company_size: (formData.company_size || '').trim(),
        size: (formData.company_size || '').trim(),
        is_featured: formData.visibility === 'featured',
        featured: formData.visibility === 'featured',
        is_top_hiring: formData.visibility === 'top_hiring',
        top_hiring: formData.visibility === 'top_hiring'
      };
      const prevEmail = String(initialFormData.email || '').trim().toLowerCase();
      const nextEmail = String(formData.email || '').trim().toLowerCase();
      if (nextEmail && nextEmail !== prevEmail) {
        try {
          const serverCompanies = await apiClient.getAdminCompanies({ search: nextEmail });
          const conflict = (serverCompanies || []).find((c: any) => {
            const cid = String(c?.id ?? c?._id ?? '');
            const email = String(c?.email || '').trim().toLowerCase();
            return email === nextEmail && cid !== id;
          });
          if (!conflict) {
            try {
              const employers = await apiClient.getEmployers({ search: nextEmail });
              const econflict = (employers || []).find((e: any) => String(e?.email || '').trim().toLowerCase() === nextEmail);
              if (econflict) {
                toast.error('Email already in use by an employer account');
                setLoading(false);
                return;
              }
            } catch { }
          }
          if (conflict) {
            toast.error('Email already in use by another company');
            setLoading(false);
            return;
          }
        } catch { }
      } else {
        try { delete payload.email; } catch { }
      }
      const hasScalarChanges = Object.keys(initialFormData).some((k) => {
        const key = k as keyof typeof initialFormData;
        if (key === 'branches') return false;
        return String(formData[key] ?? '') !== String(initialFormData[key] ?? '');
      });
      const normalizeBranches = (arr: any[]) => (arr || []).map((b: any) => ({
        id: String(b?.id || ''),
        name: String(b?.name || ''),
        city: String(b?.city || ''),
        address: String(b?.address || ''),
        phone: String(b?.phone || ''),
      }));
      const branchesChanged = JSON.stringify(normalizeBranches(formData.branches || [])) !== JSON.stringify(normalizeBranches(initialFormData.branches || []));
      const hasChanges = hasScalarChanges || branchesChanged;
      let updatedCompany = selectedCompany;
      if (hasChanges) {
        updatedCompany = await apiClient.updateCompanyByAdmin(String(id), payload);
      }
      const logoUpdated = await uploadLogoForCompany(String(id));
      if (logoUpdated) updatedCompany = logoUpdated;
      const updatedCompanies = (companies || []).map(c => (getCompanyId(c) === id ? updatedCompany : c));
      onCompanyUpdate(updatedCompanies);
      try {
        const refreshed = await apiClient.getAdminCompanies({ limit: 1000 });
        onCompanyUpdate(Array.isArray(refreshed) ? refreshed : []);
      } catch { }
      setActiveTab('list');
      setSelectedCompany(null);
      setLogoFile(null);
      toast.success('Company updated successfully');
    } catch (error: any) {
      console.error('Error updating company:', error);
      toast.error(error?.message || 'Error updating company');
    } finally {
      setLoading(false);
    }
  };

  // Delete company via admin API
  const handleDeleteCompany = async (companyId: string) => {
    if (!companyId) return;
    setLoading(true);
    try {
      await apiClient.deleteCompanyByAdmin(String(companyId), { force: true });
      const updatedCompanies = (companies || []).filter(c => getCompanyId(c) !== companyId);
      onCompanyUpdate(updatedCompanies);
      toast.success('Company deleted successfully!');
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Failed to delete company');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCompany = async (companyId: string) => {
    try {
      const id = String(companyId);
      await apiClient.approveCompanyByAdmin(id);
      const updatedCompanies = (companies || []).map(c => String(getCompanyId(c)) === id ? { ...c, verified: true } : c);
      onCompanyUpdate(updatedCompanies);
      toast.success('Company verified successfully!');
    } catch (error) {
      console.error('Error verifying company:', error);
      toast.error('Failed to verify company');
    }
  };

  const handleRejectCompany = async (companyId: string) => {
    try {
      const id = String(companyId);
      await apiClient.updateCompanyByAdmin(id, { verified: false, status: 'rejected' });
      const updatedCompanies = (companies || []).map(c => String(getCompanyId(c)) === id ? { ...c, verified: false, status: 'rejected' } : c);
      onCompanyUpdate(updatedCompanies);
      toast.success('Company verification rejected');
    } catch (error) {
      console.error('Error rejecting company:', error);
      toast.error('Failed to reject verification');
    }
  };

  const clearAllCompanies = async () => {
    try {
      const ids = (companies || []).map(getCompanyId).filter(Boolean);
      await Promise.all(ids.map((id: any) => apiClient.deleteCompanyByAdmin(String(id))));
      onCompanyUpdate([]);
      toast.success('All companies cleared');
    } catch (error) {
      console.error('Error clearing companies:', error);
      toast.error('Failed to clear companies');
    }
  };

  const [companyExportFrom, setCompanyExportFrom] = useState('');
  const [companyExportTo, setCompanyExportTo] = useState('');

  const exportCompanyData = () => {
    try {
      const from = companyExportFrom ? new Date(companyExportFrom) : null;
      const to = companyExportTo ? new Date(companyExportTo) : null;
      const rows = (companies || [])
        .filter((c: any) => {
          if (!from && !to) return true;
          const raw = c.registrationDate || c.createdAt || c.created_at || '';
          if (!raw) return true;
          const d = new Date(String(raw));
          if (from && d < from) return false;
          if (to && d > to) return false;
          return true;
        })
        .map((c: any) => {
          const cid = c.id || c._id;
          const cname = c.name || c.company_name || c.companyName || '';
          const companyJobs = (jobs || []).filter((j: any) => {
            const jcid = j.companyId || j.company_id;
            const jname = j.companyName || j.company_name || j.company;
            const idMatch = cid && jcid && String(jcid) === String(cid);
            const nameMatch = cname && jname && String(jname).toLowerCase() === String(cname).toLowerCase();
            return idMatch || nameMatch;
          });
          const activeJobs = companyJobs.filter((j: any) => String(j.status || '').toLowerCase() === 'active').length;
          const jobIds = companyJobs.map((j: any) => j.id || j._id).filter(Boolean).map(String);
          const totalApplications = (applications || []).filter((app: any) => {
            const ajid = app.jobId || app.job_id;
            return ajid && jobIds.includes(String(ajid));
          }).length;

          return {
            name: cname,
            email: c.email || '',
            phone: c.phone || c.phone_number || '',
            location: c.location || c.address || '',
            website: c.website || '',
            industry: c.industry || '',
            status: c.status || 'active',
            verified: (c.verified ?? c.is_verified ?? false) ? 'Yes' : 'No',
            totalJobs: companyJobs.length,
            activeJobs,
            applications: totalApplications
          };
        });

      const escapeHtml = (v: any) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const tableHeader = `
        <tr>
          <th>Company Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Location</th>
          <th>Website</th>
          <th>Industry</th>
          <th>Status</th>
          <th>Verified</th>
          <th>Total Jobs</th>
          <th>Active Jobs</th>
          <th>Applications</th>
        </tr>`;
      const tableRows = rows.map(r => `
        <tr>
          <td>${escapeHtml(r.name)}</td>
          <td>${escapeHtml(r.email)}</td>
          <td>${escapeHtml(r.phone)}</td>
          <td>${escapeHtml(r.location)}</td>
          <td>${escapeHtml(r.website)}</td>
          <td>${escapeHtml(r.industry)}</td>
          <td>${escapeHtml(r.status)}</td>
          <td>${escapeHtml(r.verified)}</td>
          <td>${escapeHtml(r.totalJobs)}</td>
          <td>${escapeHtml(r.activeJobs)}</td>
          <td>${escapeHtml(r.applications)}</td>
        </tr>`).join('');

      const html = `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />
            <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
          </head>
          <body>
            <table border="1">
              ${tableHeader}
              ${tableRows}
            </table>
          </body>
        </html>`;

      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `companies_export_${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Companies exported to Excel');
    } catch (e: any) {
      console.error('Export error:', e);
      toast.error(e?.message || 'Failed to export companies');
    }
  };

  const handleResetCompanies = async () => {
    const confirmed = await confirm({
      title: 'Reset Companies',
      description: 'Are you sure you want to reset all companies on the server? This action cannot be undone and will remove all company data.',
      type: 'danger',
      confirmText: 'Reset Companies',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      await apiClient.resetCompanies('all');
      // Re-fetch companies from admin API to reflect purge
      try {
        const refreshed = await apiClient.getAdminCompanies({ limit: 1000 });
        onCompanyUpdate(Array.isArray(refreshed) ? refreshed : []);
      } catch (e) {
        onCompanyUpdate([]);
      }
      toast.success('Companies reset successfully');
    } catch (error: any) {
      console.error('Error resetting companies:', error);
      toast.error(error?.message || 'Error resetting companies');
    } finally {
      setLoading(false);
    }
  };

  const handleResetEmployers = async () => {
    const confirmed = await confirm({
      title: 'Reset Employer Accounts',
      description: 'Are you sure you want to reset all employer accounts and their related companies? This will permanently delete all employer-related data.',
      type: 'danger',
      confirmText: 'Reset Employers',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      await apiClient.resetEmployers();
      // Refresh companies since employer reset also removes companies
      try {
        const refreshed = await apiClient.getAdminCompanies({ limit: 100 });
        onCompanyUpdate(Array.isArray(refreshed) ? refreshed : []);
      } catch (e) {
        onCompanyUpdate([]);
      }
      toast.success('Employers reset successfully');
    } catch (error: any) {
      console.error('Error resetting employers:', error);
      toast.error(error?.message || 'Error resetting employers');
    } finally {
      setLoading(false);
    }
  };
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { class: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { class: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { class: 'bg-red-100 text-red-800', label: 'Suspended' },
      pending: { class: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      rejected: { class: 'bg-orange-100 text-orange-800', label: 'Rejected' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Employer Management</h2>
          <p className="text-gray-600">Manage company profiles, verification, and activities</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={companyExportFrom} onChange={(e) => setCompanyExportFrom(e.target.value)} className="h-9" />
          <span className="text-sm text-gray-500">to</span>
          <Input type="date" value={companyExportTo} onChange={(e) => setCompanyExportTo(e.target.value)} className="h-9" />
          <Button variant="outline" onClick={exportCompanyData}>
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1">
        <Card>
          <CardContent className="p-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">Total Companies</p>
                <p className="text-sm font-bold text-blue-600">{enhancedCompanies.length}</p>
              </div>
              <Building2 className="h-3 w-3 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">Verified</p>
                <p className="text-sm font-bold text-green-600">
                  {enhancedCompanies.filter(c => c.verified).length}
                </p>
              </div>
              <CheckCircle className="h-3 w-3 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">Pending Verification</p>
                <p className="text-sm font-bold text-orange-600">
                  {enhancedCompanies.filter(c => !c.verified).length}
                </p>
              </div>
              <XCircle className="h-3 w-3 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-gray-600">Active Jobs</p>
                <p className="text-sm font-bold text-purple-600">
                  {enhancedCompanies.reduce((sum, c) => sum + c.activeJobs, 0)}
                </p>
              </div>
              <Briefcase className="h-3 w-3 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="list">Company List</TabsTrigger>
          <TabsTrigger value="verification">Verification Queue</TabsTrigger>
          <TabsTrigger value="add">+ Add Company</TabsTrigger>
        </TabsList>

        {/* Inline Add Company */}
        <TabsContent value="add">
          <Card>
            <CardHeader>
              <CardTitle>Add New Company</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Box label="Company Name *">
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Mega Job Nepal Pvt. Ltd." className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <Box label="Email *">
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="company@example.com" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <Box label="Phone">
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+977-1-XXXXXXX" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <Box label="Website">
                    <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://example.com" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Box label="Country">
                        <div className="flex items-center gap-2 h-full w-full">
                          {isCustomCountry ? (
                            <Input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} placeholder="Enter country" className="flex-1 h-full px-2 bg-transparent border-0 text-sm focus:outline-none" />
                          ) : (
                            <select value={formData.country || 'Nepal'} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="flex-1 h-full px-1 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                              {(adminCountries && adminCountries.length ? adminCountries : COUNTRIES_FALLBACK).map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          )}
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-gray-500 hover:bg-gray-100" onClick={() => setIsCustomCountry(v => !v)}>
                            {isCustomCountry ? 'List' : 'Other'}
                          </Button>
                        </div>
                      </Box>
                      <Box label="Province">
                        <div className="flex items-center gap-2 h-full w-full">
                          {isCustomProvince || !(provinceOptions || []).length ? (
                            <Input value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} placeholder="Enter province" className="flex-1 h-full px-2 bg-transparent border-0 text-sm focus:outline-none" />
                          ) : (
                            <select value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} className="flex-1 h-full px-1 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                              <option value="">Select province</option>
                              {(provinceOptions || []).map(p => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          )}
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-gray-500 hover:bg-gray-100" onClick={() => setIsCustomProvince(v => !v)}>
                            {isCustomProvince ? 'List' : 'Other'}
                          </Button>
                        </div>
                      </Box>
                      <Box label="City">
                        <div className="flex items-center gap-2 h-full w-full">
                          {isCustomCity || !(cityOptions || []).length ? (
                            <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Enter city" className="flex-1 h-full px-2 bg-transparent border-0 text-sm focus:outline-none" />
                          ) : (
                            <select value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="flex-1 h-full px-1 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                              <option value="">Select city</option>
                              {cityOptions.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          )}
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-gray-500 hover:bg-gray-100" onClick={() => setIsCustomCity(v => !v)}>
                            {isCustomCity ? 'List' : 'Other'}
                          </Button>
                        </div>
                      </Box>
                      <Box label="Postal Code">
                        <Input value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder="e.g., 44600" />
                      </Box>
                      <Box label="Company Size">
                        <select value={formData.company_size} onChange={(e) => setFormData({ ...formData, company_size: e.target.value })} className="w-full h-full px-2 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                          <option value="">Select size</option>
                          {['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'].map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </Box>
                    </div>
                  </div>
                  <Box label="Industry">
                    <select value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} className="w-full h-full px-2 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                      <option value="">Select industry</option>
                      {industryOptions.map(i => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </Box>
                  <Box label="Visibility">
                    <select value={formData.visibility} onChange={(e) => setFormData({ ...formData, visibility: e.target.value as FormDataState['visibility'] })} className="w-full h-full px-2 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                      <option value="default">Default</option>
                      <option value="featured">Featured Companies</option>
                      <option value="top_hiring">Top Hiring Companies</option>
                    </select>
                  </Box>
                  {/* Description + Branches side by side */}
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <BoxAuto label="Description">
                        <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description about the company" />
                      </BoxAuto>
                    </div>

                    {/* Branch Management Section */}
                    <div>
                      <BoxAuto label={`Company Branches (${formData.branches.length})`}>
                        {formData.branches.map((branch, idx) => (
                          <div key={branch.id} className="p-3 bg-gray-50 border border-gray-200 rounded-md mb-2">
                            {editingBranchId === branch.id ? (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-semibold text-gray-700">Branch {idx + 1}</h5>
                                  <div className="flex items-center gap-2">
                                    <Button type="button" size="sm" variant="outline" onClick={() => setEditingBranchId(null)}>Save</Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const arr = formData.branches.filter((_, i) => i !== idx);
                                        setFormData({ ...formData, branches: arr });
                                      }}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-xs">Branch Name *</Label>
                                    <Input
                                      value={branch.name}
                                      onChange={(e) => {
                                        const arr = [...formData.branches];
                                        arr[idx] = { ...arr[idx], name: e.target.value };
                                        setFormData({ ...formData, branches: arr });
                                      }}
                                      placeholder="e.g., Main Office"
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">City *</Label>
                                    <Input
                                      value={branch.city}
                                      onChange={(e) => {
                                        const arr = [...formData.branches];
                                        arr[idx] = { ...arr[idx], city: e.target.value };
                                        setFormData({ ...formData, branches: arr });
                                      }}
                                      placeholder="e.g., Kathmandu"
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Phone</Label>
                                    <Input
                                      value={branch.phone || ''}
                                      onChange={(e) => {
                                        const arr = [...formData.branches];
                                        arr[idx] = { ...arr[idx], phone: e.target.value };
                                        setFormData({ ...formData, branches: arr });
                                      }}
                                      placeholder="e.g., +977-1-4234567"
                                      className="mt-1"
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label className="text-xs">Full Address</Label>
                                    <Input
                                      value={branch.address}
                                      onChange={(e) => {
                                        const arr = [...formData.branches];
                                        arr[idx] = { ...arr[idx], address: e.target.value };
                                        setFormData({ ...formData, branches: arr });
                                      }}
                                      placeholder="e.g., New Road, Kathmandu"
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Employees</Label>
                                    <Input
                                      type="number"
                                      value={branch.employees || ''}
                                      onChange={(e) => {
                                        const arr = [...formData.branches];
                                        arr[idx] = { ...arr[idx], employees: e.target.value ? Number(e.target.value) : undefined };
                                        setFormData({ ...formData, branches: arr });
                                      }}
                                      placeholder="e.g., 50"
                                      className="mt-1"
                                      min="0"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <input
                                    type="checkbox"
                                    id={`hq-${idx}`}
                                    checked={branch.is_headquarters || false}
                                    onChange={(e) => {
                                      const arr = [...formData.branches];
                                      arr[idx] = { ...arr[idx], is_headquarters: e.target.checked };
                                      setFormData({ ...formData, branches: arr });
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <Label htmlFor={`hq-${idx}`} className="cursor-pointer text-sm">
                                    Mark as Headquarters
                                  </Label>
                                </div>
                              </>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="font-semibold text-gray-800">{branch.name || `Branch ${idx + 1}`}</span>
                                  <span className="text-sm text-gray-600">{branch.city || '—'}</span>
                                  {branch.phone ? (<span className="text-sm text-gray-500">{branch.phone}</span>) : null}
                                  {typeof branch.employees === 'number' ? (
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700">{branch.employees} employees</span>
                                  ) : null}
                                  {branch.is_headquarters ? (
                                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">HQ</span>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button type="button" size="sm" variant="outline" onClick={() => setEditingBranchId(branch.id)}>Edit</Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const arr = formData.branches.filter((_, i) => i !== idx);
                                      setFormData({ ...formData, branches: arr });
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        <div className="w-full bg-gray-50 border border-gray-200 rounded-md h-10 flex items-center justify-between px-3 mt-1">
                          <span className="text-sm font-semibold text-gray-700">Add Branch</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newId = Math.random().toString(36).slice(2);
                              setFormData({
                                ...formData,
                                branches: [
                                  ...formData.branches,
                                  {
                                    id: newId,
                                    name: '',
                                    city: '',
                                    address: '',
                                    phone: '',
                                    employees: undefined,
                                    is_headquarters: false
                                  }
                                ]
                              });
                              setEditingBranchId(newId);
                            }}
                            className="gap-2"
                          >
                            <Plus className="h-4 w-4" /> Add
                          </Button>
                        </div>
                      </BoxAuto>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Logo</label>
                    <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
                    {logoFile && (
                      <Button type="button" variant="ghost" className="mt-2" onClick={() => setLogoFile(null)}>Remove</Button>
                    )}
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button variant="outline" onClick={() => setActiveTab('list')}>Cancel</Button>
                  <Button onClick={handleAddCompany} disabled={loading}>
                    {loading ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Adding...</>) : 'Add Company'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company List */}
        <TabsContent value="list" className="space-y-6">
          {ConfirmDialog}
          {/* Search and Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search companies, locations, industries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-md shadow-none hover:bg-transparent focus:outline-none focus:ring-0"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <select
                    value={verificationFilter}
                    onChange={(e) => setVerificationFilter(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-md shadow-none hover:bg-transparent focus:outline-none focus:ring-0"
                  >
                    <option value="all">All Verification</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                  </select>
                  <select
                    value={industryFilter}
                    onChange={(e) => setIndustryFilter(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-md shadow-none hover:bg-transparent focus:outline-none focus:ring-0"
                  >
                    <option value="all">All Industries</option>
                    {industryOptions.map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    className="whitespace-nowrap"
                    onClick={() => {
                      setActiveTab('list');
                      setVerificationFilter('unverified');
                    }}
                  >
                    Unverified only
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Companies Table */}
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Companies ({filteredCompanies.length})</CardTitle>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setActiveTab('add')}>
                <Upload className="h-4 w-4 mr-2" />
                Add Company
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1.5 px-2.5 text-xs">#</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Company</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Contact</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Industry</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Jobs</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Applications</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Status</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Verified</th>
                      <th className="text-left py-1.5 px-2.5 text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCompanies.map((company, idx) => (
                      <tr key={getCompanyId(company) || getCompanyName(company)} className="border-b hover:bg-gray-50 odd:bg-white even:bg-orange-100/50">
                        <td className="py-0.5 px-3 text-[13px] text-gray-600">
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        <td className="py-0.5 px-2">
                          <div className="flex items-center space-x-2">
                            {normalizedCompanyLogo(company) ? (
                              <div className="w-6 h-6 overflow-hidden border-gray-100">
                                <ImageWithFallback
                                  src={normalizedCompanyLogo(company)}
                                  fallbackSrc={'/CompanyLogo.png'}
                                  alt={getCompanyName(company) || 'Company Logo'}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            ) : (
                              <div className="w-6 h-6 bg-blue-100 flex items-center justify-center">
                                <Building2 className="h-3.5 w-3.5 text-blue-600" />
                              </div>
                            )}
                            <div className="min-w-0 max-w-[300px]">
                              <p
                                className="text-[13px] font-medium text-gray-900 whitespace-nowrap overflow-x-auto overflow-y-hidden leading-tight"
                                title={getCompanyName(company)}
                              >
                                {getCompanyName(company)}
                              </p>
                              <p
                                className="text-[11px] text-gray-500 flex items-center whitespace-nowrap overflow-x-auto overflow-y-hidden"
                                title={safeString((company as any).location)}
                              >
                                <MapPin className="h-2.5 w-2.5 mr-0.5" />
                                {safeString((company as any).location)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-0.5 px-2">
                          <div className="text-[11px] leading-tight max-w-[250px]">
                            <p className="flex items-center whitespace-nowrap overflow-x-auto" title={safeString(company.email)}>
                              <Mail className="h-2.5 w-2.5 mr-1" />
                              {company.email}
                            </p>
                            <p className="flex items-center text-gray-500 whitespace-nowrap overflow-x-auto" title={safeString(company.phone)}>
                              <Phone className="h-2.5 w-2.5 mr-1" />
                              {company.phone}
                            </p>
                          </div>
                        </td>
                        <td className="py-0.5 px-2.5">
                          <Badge variant="outline" className="text-[10px] px-1.5 h-5">{company.industry}</Badge>
                        </td>
                        <td className="py-0.5 px-2.5">
                          <div className="text-[11px] leading-tight text-center">
                            <p className="font-semibold text-blue-600">{company.activeJobs}</p>
                            <p className="text-gray-500 scale-90">Jobs</p>
                          </div>
                        </td>
                        <td className="py-0.5 px-2.5">
                          <div className="text-[11px] leading-tight text-center">
                            <p className="font-semibold text-purple-600">{company.totalApplications}</p>
                            <p className="text-gray-500 scale-90">Apps</p>
                          </div>
                        </td>
                        <td className="py-0.5 px-2.5">
                          {React.cloneElement(getStatusBadge(company.status) as React.ReactElement, { className: 'text-[10px] px-1.5 h-5' })}
                        </td>
                        <td className="py-0.5 px-2.5">
                          {company.verified ? (
                            <Badge className="bg-green-100 text-green-800 text-[10px] px-1.5 h-5 shadow-none border-0">
                              <CheckCircle className="h-2.5 w-2.5 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 text-[10px] px-1.5 h-5 shadow-none border-0">
                              <XCircle className="h-2.5 w-2.5 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </td>
                        <td className="py-0.5 px-2.5">
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => {
                                setSelectedCompany(company);
                                setActiveTab('profile');
                              }}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => openEditModal(company)}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            {!company.verified && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleVerifyCompany(getCompanyId(company))}
                              >
                                <CheckCircle className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={async () => {
                                const id = String(getCompanyId(company));
                                const name = getCompanyName(company);
                                const confirmed = await confirm({
                                  title: 'Delete Company',
                                  description: 'Are you sure you want to delete this company? This action cannot be undone. All associated data will be permanently removed.',
                                  type: 'danger',
                                  itemName: name,
                                  confirmText: 'Delete Company',
                                  cancelText: 'Cancel'
                                });
                                if (confirmed) {
                                  await handleDeleteCompany(id);
                                }
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              {filteredCompanies.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t mt-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="px-2 py-1  bg-white"
                    >
                      {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span>entries</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Showing {filteredCompanies.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}
                    {" "}to{" "}
                    {Math.min(currentPage * itemsPerPage, filteredCompanies.length)} of {filteredCompanies.length} entries
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 7).map(page => (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    ))}
                    {totalPages > 7 && (
                      <>
                        <span className="px-2 text-gray-500">...</span>
                        <Button
                          variant={totalPages === currentPage ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verification Queue */}
        <TabsContent value="verification" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pending Verification ({enhancedCompanies.filter(c => !c.verified).length})</CardTitle>
              <p className="text-sm text-gray-600">Review and verify company registrations</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {enhancedCompanies.filter(c => !c.verified).map(company => (
                  <div key={company.id} className=" p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          {normalizedCompanyLogo(company) ? (
                            <div className="w-12 h-12  overflow-hidden border-gray-200">
                              <ImageWithFallback
                                src={normalizedCompanyLogo(company)}
                                alt={(company.name || 'Company Logo') as string}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-blue-100  flex items-center justify-center">
                              <Building2 className="h-6 w-6 text-blue-600" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold text-lg">{company.name}</h3>
                            <p className="text-gray-600">{company.industry}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-sm font-medium text-gray-700">Contact Information</p>
                            <p className="text-sm text-gray-600">{company.email}</p>
                            <p className="text-sm text-gray-600">{company.phone}</p>
                            <p className="text-sm text-gray-600">{company.location}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">Company Details</p>
                            <p className="text-sm text-gray-600">Employees: {company.employeeCount}</p>
                            <p className="text-sm text-gray-600">Website: {company.website}</p>
                            <p className="text-sm text-gray-600">Registered: {company.registrationDate}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">Activity</p>
                            <p className="text-sm text-gray-600">Jobs Posted: {company.totalJobs}</p>
                            <p className="text-sm text-gray-600">Applications: {company.totalApplications}</p>
                            <p className="text-sm text-gray-600">Last Active: {company.lastActivity}</p>
                          </div>
                        </div>

                        <div className="bg-gray-50  p-3 mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Company Description</p>
                          <p className="text-sm text-gray-600">
                            {company.description || "No description provided by the company."}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          onClick={() => handleVerifyCompany(getCompanyId(company))}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Verify
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleRejectCompany(getCompanyId(company))}
                          className="text-red-600 hover:text-red-700 border-red-200"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCompany(company);
                            setActiveTab('profile');
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Details
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Edit Company Tab */}
        <TabsContent value="edit">
          <Card>
            <CardHeader>
              <CardTitle>Edit Company</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Box label="Company Name *">
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Mega Job Nepal Pvt. Ltd." className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <Box label="Email *">
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="company@example.com" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <Box label="Phone">
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+977-1-XXXXXXX" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <Box label="Website">
                    <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://example.com" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                  </Box>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Box label="Country">
                        <div className="flex items-center gap-2 h-full w-full">
                          {isCustomCountry ? (
                            <Input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} placeholder="Enter country" className="flex-1 h-full px-2 bg-transparent border-0 text-sm focus:outline-none" />
                          ) : (
                            <select value={formData.country || 'Nepal'} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="flex-1 h-full px-1 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                              {(adminCountries && adminCountries.length ? adminCountries : COUNTRIES_FALLBACK).map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          )}
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-gray-500 hover:bg-gray-100" onClick={() => setIsCustomCountry(v => !v)}>
                            {isCustomCountry ? 'List' : 'Other'}
                          </Button>
                        </div>
                      </Box>
                      <Box label="Province">
                        <div className="flex items-center gap-2 h-full w-full">
                          {isCustomProvince || !(provinceOptions || []).length ? (
                            <Input value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} placeholder="Enter province" className="flex-1 h-full px-2 bg-transparent border-0 text-sm focus:outline-none" />
                          ) : (
                            <select value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} className="flex-1 h-full px-1 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                              <option value="">Select province</option>
                              {(provinceOptions || []).map(p => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          )}
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-gray-500 hover:bg-gray-100" onClick={() => setIsCustomProvince(v => !v)}>
                            {isCustomProvince ? 'List' : 'Other'}
                          </Button>
                        </div>
                      </Box>
                      <Box label="City">
                        <div className="flex items-center gap-2 h-full w-full">
                          {isCustomCity || !(cityOptions || []).length ? (
                            <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Enter city" className="flex-1 h-full px-2 bg-transparent border-0 text-sm focus:outline-none" />
                          ) : (
                            <select value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="flex-1 h-full px-1 bg-transparent border-0 text-sm focus:outline-none cursor-pointer">
                              <option value="">Select city</option>
                              {cityOptions.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          )}
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-gray-500 hover:bg-gray-100" onClick={() => setIsCustomCity(v => !v)}>
                            {isCustomCity ? 'List' : 'Other'}
                          </Button>
                        </div>
                      </Box>
                      <Box label="Postal Code">
                        <Input value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder="e.g., 44600" className="w-full h-full px-3 bg-transparent border-0 rounded-none text-sm focus:outline-none" />
                      </Box>
                    </div>
                  </div>
                  <Box label="Industry">
                    <select
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="w-full h-full px-2 bg-transparent border-0 text-sm focus:outline-none cursor-pointer"
                    >
                      <option value="">Select industry</option>
                      {industryOptions.map(i => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </Box>
                  <Box label="Company Size">
                    <select
                      value={formData.company_size}
                      onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                      className="w-full h-full px-2 bg-transparent border-0 text-sm focus:outline-none cursor-pointer"
                    >
                      <option value="">Select size</option>
                      {['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Box>
                  <Box label="Visibility">
                    <select
                      value={formData.visibility}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as FormDataState['visibility'] })}
                      className="w-full h-full px-2 bg-transparent border-0 text-sm focus:outline-none cursor-pointer"
                    >
                      <option value="default">Default</option>
                      <option value="featured">Featured Companies</option>
                      <option value="top_hiring">Top Hiring Companies</option>
                    </select>
                  </Box>
                  <div className="md:col-span-2">
                    <BoxAuto label="Description">
                      <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description about the company" />
                    </BoxAuto>
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        Company Branches ({formData.branches.length})
                      </label>
                    </div>
                    {formData.branches.map((branch, idx) => (
                      <div key={branch.id} className="p-4  bg-gray-50 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-gray-700">Branch {idx + 1}</h5>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const arr = formData.branches.filter((_, i) => i !== idx);
                              setFormData({ ...formData, branches: arr });
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Branch Name *</Label>
                            <Input
                              value={branch.name}
                              onChange={(e) => {
                                const arr = [...formData.branches];
                                arr[idx] = { ...arr[idx], name: e.target.value };
                                setFormData({ ...formData, branches: arr });
                              }}
                              placeholder="e.g., Main Office"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">City *</Label>
                            <Input
                              value={branch.city}
                              onChange={(e) => {
                                const arr = [...formData.branches];
                                arr[idx] = { ...arr[idx], city: e.target.value };
                                setFormData({ ...formData, branches: arr });
                              }}
                              placeholder="e.g., Kathmandu"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Phone</Label>
                            <Input
                              value={branch.phone || ''}
                              onChange={(e) => {
                                const arr = [...formData.branches];
                                arr[idx] = { ...arr[idx], phone: e.target.value };
                                setFormData({ ...formData, branches: arr });
                              }}
                              placeholder="e.g., +977-1-4234567"
                              className="mt-1"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Full Address</Label>
                            <Input
                              value={branch.address}
                              onChange={(e) => {
                                const arr = [...formData.branches];
                                arr[idx] = { ...arr[idx], address: e.target.value };
                                setFormData({ ...formData, branches: arr });
                              }}
                              placeholder="e.g., New Road, Kathmandu"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Employees</Label>
                            <Input
                              type="number"
                              value={branch.employees || ''}
                              onChange={(e) => {
                                const arr = [...formData.branches];
                                arr[idx] = { ...arr[idx], employees: e.target.value ? Number(e.target.value) : undefined };
                                setFormData({ ...formData, branches: arr });
                              }}
                              placeholder="e.g., 50"
                              className="mt-1"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id={`edit-hq-${idx}`}
                            checked={branch.is_headquarters || false}
                            onChange={(e) => {
                              const arr = [...formData.branches];
                              arr[idx] = { ...arr[idx], is_headquarters: e.target.checked };
                              setFormData({ ...formData, branches: arr });
                            }}
                            className="w-4 h-4"
                          />
                          <Label htmlFor={`edit-hq-${idx}`} className="cursor-pointer text-sm">
                            Mark as Headquarters
                          </Label>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          branches: [
                            ...formData.branches,
                            {
                              id: Math.random().toString(36).slice(2),
                              name: '',
                              city: '',
                              address: '',
                              phone: '',
                              employees: undefined,
                              is_headquarters: false
                            }
                          ]
                        });
                      }}
                      className="w-full gap-2 border-dashed border-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Branch
                    </Button>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Logo</label>
                    <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
                    {logoFile && (
                      <Button type="button" variant="ghost" className="mt-2" onClick={() => setLogoFile(null)}>Remove</Button>
                    )}
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button variant="outline" onClick={() => setActiveTab('list')}>Cancel</Button>
                  <Button onClick={handleEditCompany} disabled={loading}>{loading ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Saving...</>) : 'Save Changes'}</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Profile</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCompany ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    {normalizedCompanyLogo(selectedCompany) ? (
                      <div className="w-12 h-12  overflow-hidden border-gray-200">
                        <ImageWithFallback
                          src={normalizedCompanyLogo(selectedCompany)}
                          fallbackSrc={'/CompanyLogo.png'}
                          alt={getCompanyName(selectedCompany)}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-blue-100  flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-lg">{getCompanyName(selectedCompany)}</p>
                      <p className="text-sm text-gray-600 flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {safeString((selectedCompany as any).location)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm"><strong>Email:</strong> {selectedCompany.email || ''}</p>
                      <p className="text-sm"><strong>Phone:</strong> {selectedCompany.phone || ''}</p>
                      <p className="text-sm"><strong>Website:</strong> {selectedCompany.website || ''}</p>
                      <p className="text-sm"><strong>Industry:</strong> {selectedCompany.industry || ''}</p>
                    </div>
                    <div>
                      <p className="text-sm"><strong>Status:</strong> {selectedCompany.status || 'active'}</p>
                      <p className="text-sm"><strong>Verified:</strong> {selectedCompany.verified ? 'Yes' : 'No'}</p>
                      <p className="text-sm"><strong>Active Jobs:</strong> {(selectedCompany as any).activeJobs ?? 0}</p>
                      <p className="text-sm"><strong>Total Applications:</strong> {(selectedCompany as any).totalApplications ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setActiveTab('list')}>Back to List</Button>
                    <Button onClick={() => openEditModal(selectedCompany)}>
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">No company selected</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}
